import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { EmptyState } from './sectionShared';
import {
  buildMilestoneGroups,
  buildTotalsRow,
  getColumnClassName,
  getOrderedColumns,
  PROJECT_MILESTONE_TABLE_FLAGS,
  renderExpandableIdentityCell,
} from './projectMilestoneTableParts';

export {
  PROJECT_MILESTONE_COLUMNS,
  PROJECT_MILESTONE_TABLE_FLAGS,
} from './projectMilestoneTableParts';

const ProjectMilestoneTable = ({
  rows,
  flags,
  totals,
  isLoading,
  emptyMessage,
  onMilestoneEdit,
  onInvoiceAdd,
  onInvoiceEdit,
  onCollectionAdd,
  onCollectionEdit,
}) => {
  const columns = getOrderedColumns(flags);
  const [expandedGroups, setExpandedGroups] = useState({});

  const groupedRows = useMemo(() => buildMilestoneGroups(rows), [rows]);

  const visibleRows = useMemo(() => groupedRows.flatMap((group) => {
    const isExpanded = Boolean(expandedGroups[group.key]);
    const parentRow = {
      ...group.parent,
      _groupKey: group.key,
      _isGroupParent: true,
      _hasChildren: group.children.length > 0,
      _isExpanded: isExpanded,
    };

    if (!isExpanded || !group.children.length) return [parentRow];

    const childRows = group.children.map((child) => ({
      ...child,
      po_number: '',
      po_link: '',
      milestone_id: '',
      _groupKey: group.key,
      _isGroupParent: false,
      _isGroupChild: true,
      _hasChildren: false,
      _isExpanded: false,
    }));

    return [parentRow, ...childRows];
  }), [expandedGroups, groupedRows]);

  const firstIdentityColumnKey = useMemo(() => {
    if (columns.some((column) => column.key === 'po_number')) return 'po_number';
    return 'milestone_id';
  }, [columns]);

  const toggleGroup = (groupKey) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  const totalsRow = useMemo(
    () => buildTotalsRow(totals, columns),
    [totals, columns],
  );

  if (isLoading) {
    return (
      <div className="risk-table-wrapper milestone-table-wrapper">
        <div className="milestone-table-state">Loading milestone table...</div>
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div className="risk-table-wrapper milestone-table-wrapper">
        <div className="milestone-table-state">
          <EmptyState text={emptyMessage} />
        </div>
      </div>
    );
  }

  return (
    <div className="risk-table-wrapper milestone-table-wrapper">
      <div className="risk-table-scroll milestone-table-scroll">
        <table className="milestone-data-table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={[
                    'th-cell',
                    getColumnClassName(column.key),
                    column.className === 'td-actions' ? 'th-actions' : '',
                    column.sticky ? 'is-sticky-column' : '',
                  ].join(' ').trim()}
                  scope="col"
                >
                  <div className={`th-content ${column.className === 'td-actions' ? 'th-actions-content' : ''}`.trim()}>
                    <span>{column.label}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row) => (
              <tr
                key={row.row_id}
                className={[
                  'tr-data',
                  `row-type-${row.row_type || 'default'}`,
                  row._isGroupChild ? 'child-row' : '',
                ].join(' ').trim()}
              >
                {columns.map((column) => (
                  <td
                    key={`${row.row_id}-${column.key}`}
                    className={[
                      'td-cell',
                      getColumnClassName(column.key),
                      column.className || '',
                      column.sticky ? 'is-sticky-column' : '',
                      row.row_type ? `row-cell-${row.row_type}` : '',
                    ].join(' ').trim()}
                  >
                    <span className={['milestone-cell-text', column.contentClassName || 'is-text'].join(' ').trim()}>
                      {renderExpandableIdentityCell({
                        row,
                        renderedValue: column.key === 'actions'
                          ? column.render(row, {
                            onMilestoneEdit,
                            onInvoiceAdd,
                            onInvoiceEdit,
                            onCollectionAdd,
                            onCollectionEdit,
                          })
                          : column.render(row),
                        firstIdentityColumnKey,
                        columnKey: column.key,
                        onToggle: toggleGroup,
                      })}
                    </span>
                  </td>
                ))}
              </tr>
            ))}
            <tr className="tr-data row-type-totals totals-row">
              {columns.map((column) => (
                <td
                  key={`${totalsRow.row_id}-${column.key}`}
                  className={[
                    'td-cell',
                    getColumnClassName(column.key),
                    column.className || '',
                    column.sticky ? 'is-sticky-column' : '',
                    'row-cell-totals',
                  ].join(' ').trim()}
                >
                  <span className={['milestone-cell-text', column.contentClassName || 'is-text'].join(' ').trim()}>
                    {column.render(totalsRow, {
                      onInvoiceAdd,
                      onMilestoneEdit,
                      onInvoiceEdit,
                      onCollectionAdd,
                      onCollectionEdit,
                    })}
                  </span>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

ProjectMilestoneTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.shape({
    row_id: PropTypes.string.isRequired,
    row_type: PropTypes.string,
    actions: PropTypes.shape({
      edit: PropTypes.bool,
      add: PropTypes.bool,
    }),
  })),
  flags: PropTypes.shape({
    po_number_view: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    po_link_view: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    ms_v: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    ms_amt: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    replanned_due_date_view: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    ms_inv: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    ms_rdt: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    ms_rmk: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    collection_amount_view: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    collection_date_view: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    ms_e: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    ms_a: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  }),
  totals: PropTypes.shape({
    contractValuePercent: PropTypes.number,
    amount: PropTypes.number,
    invoiceAmount: PropTypes.number,
    invoiceAmountWithTax: PropTypes.number,
    amountToCollect: PropTypes.number,
    collectedAmount: PropTypes.number,
  }),
  isLoading: PropTypes.bool,
  emptyMessage: PropTypes.string,
  onMilestoneEdit: PropTypes.func,
  onInvoiceAdd: PropTypes.func,
  onInvoiceEdit: PropTypes.func,
  onCollectionAdd: PropTypes.func,
  onCollectionEdit: PropTypes.func,
};

ProjectMilestoneTable.defaultProps = {
  rows: [],
  flags: PROJECT_MILESTONE_TABLE_FLAGS,
  totals: null,
  isLoading: false,
  emptyMessage: 'No milestone data available',
  onMilestoneEdit: undefined,
  onInvoiceAdd: undefined,
  onInvoiceEdit: undefined,
  onCollectionAdd: undefined,
  onCollectionEdit: undefined,
};

export default ProjectMilestoneTable;
